import type { CSSProperties, MouseEventHandler, ReactNode } from 'react'

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export type ModalVariant =
  | 'default'
  | 'confirmation'
  | 'notice'
  | 'fileDetails'
  | 'moveFiles'

export type ModalIconTone = 'warning' | 'danger' | 'success'

export type ModalDetailItem = {
  label: ReactNode
  value: ReactNode
}

export type ModalFolderItem = {
  id: string
  label: string
  icon?: ReactNode
}

export const MODAL_SIZES: ModalSize[] = ['sm', 'md', 'lg', 'xl']

export const MODAL_SIZE_LABELS: Record<ModalSize, string> = {
  xs: 'Very small (432px)',
  sm: 'Small (480px)',
  md: 'Medium (640px)',
  lg: 'Large (960px)',
  xl: 'Extra large (1120px)',
}

export const MODAL_VARIANTS: ModalVariant[] = [
  'default',
  'confirmation',
  'notice',
  'fileDetails',
  'moveFiles',
]

export const MODAL_VARIANT_LABELS: Record<ModalVariant, string> = {
  default: 'Default',
  confirmation: 'Confirmation',
  notice: 'Notice',
  fileDetails: 'File details',
  moveFiles: 'Move files',
}

export const MODAL_FILE_DETAILS_DEMO_ITEMS: ModalDetailItem[] = [
  { label: 'Date uploaded', value: '08/04/2026' },
  { label: 'Uploaded by', value: 'Thomas Jones' },
  { label: 'Attached to claims', value: 'No' },
  { label: 'Attached to forms', value: 'No' },
]

export const MODAL_MOVE_FILES_DEMO_FOLDERS: ModalFolderItem[] = [
  { id: 'claims', label: 'Claims' },
  { id: 'forms-and-letters', label: 'Forms and letters' },
  { id: 'images', label: 'Images' },
  { id: 'x-ray-files', label: 'X-ray files' },
]

export type ModalProps = {
  open?: boolean
  onClose?: () => void
  size?: ModalSize
  variant?: ModalVariant
  title: string
  description?: string | string[]
  children?: ReactNode
  icon?: ReactNode
  iconTone?: ModalIconTone
  /** File details variant — leading file type icon (e.g. PDF). */
  fileIcon?: ReactNode
  /** File details variant — primary file name (link-styled). */
  fileName?: ReactNode
  /** File details variant — secondary line under the name (type + size). */
  fileMeta?: ReactNode
  /** File details variant — label/value rows under the file summary. */
  details?: ModalDetailItem[]
  /** Move files variant — folders shown in the picker list. */
  folders?: ModalFolderItem[]
  /** Move files variant — controlled selected folder id. */
  selectedFolderId?: string
  /** Move files variant — uncontrolled initial selection. */
  defaultSelectedFolderId?: string
  onFolderSelect?: (folderId: string) => void
  /** Move files variant — controlled search value. */
  folderSearchValue?: string
  defaultFolderSearchValue?: string
  onFolderSearchChange?: (value: string) => void
  folderSearchPlaceholder?: string
  showClose?: boolean
  closeOnBackdrop?: boolean
  primaryLabel?: string
  secondaryLabel?: string
  secondaryRightIcon?: ReactNode
  primaryDestructive?: boolean
  primaryDisabled?: boolean
  onPrimaryClick?: MouseEventHandler<HTMLButtonElement>
  onSecondaryClick?: MouseEventHandler<HTMLButtonElement>
  footerStart?: ReactNode
  showFooter?: boolean
  /** Renders inside the showcase stage without portaling to `document.body`. */
  inline?: boolean
  /** Dark theme surface. */
  darkMode?: boolean
  className?: string
  bodyClassName?: string
  style?: CSSProperties
  'aria-label'?: string
}
